import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Services } from '@ekhodealer/ekho-common';

import type { ServicesProvider } from '@ekhodealer/ekho-common';
import type { UseMutationResult } from '@tanstack/react-query';
import type { UpdateAiBdcSettings } from '../types';

import { apiClient } from '@config/server/apiClient';
import { configurationEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

import { aiBdc } from './queryKeys';

/**
 * API function to update BDC settings for a services provider.
 * Returns the full ServicesProvider from the backend.
 */
const updateBdcSettings = async (
	servicesProviderId: string,
	data: UpdateAiBdcSettings,
): Promise<ServicesProvider> => {
	const response = await apiClient.post<ServicesProvider>(
		configurationEndpoints.bdc(servicesProviderId),
		data,
	);
	return response.data;
};

/**
 * Hook for updating BDC settings for a services provider.
 *
 * On success:
 * 1. Updates the ServicesProvider detail cache with the full SP returned by the API
 * 2. Derives the BDC settings from the returned SP and sets the BDC settings cache directly,
 *    avoiding an extra network round-trip.
 */
export const useUpdateAiBdcSettings = (
	spId: string,
): UseMutationResult<ServicesProvider, Error, UpdateAiBdcSettings> => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: UpdateAiBdcSettings): Promise<ServicesProvider> =>
			updateBdcSettings(spId, data),
		onSuccess: (returnedSp) => {
			queryClient.setQueryData(
				queries.servicesProviders.detail(returnedSp.id).queryKey,
				returnedSp,
			);
			queryClient.setQueryData(
				aiBdc.settings(spId).queryKey,
				returnedSp.config?.[Services.BDC],
			);
		},
	});
};
